// src/pages/CustomerDashboard.jsx
import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

// Helper: Build line chart data based on filter
const buildLineData = (dailyRecords, filter) => {
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);

  let relevantDays = [];

  // ------- FILTER ---------
  switch (filter) {
    case "Today":
      relevantDays = dailyRecords.filter(d => d.date === todayStr);
      break;
    case "Yesterday": {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      relevantDays = dailyRecords.filter(d => d.date === y.toISOString().slice(0, 10));
      break;
    }
    case "Weekly": {
      const start = new Date(now);
      start.setHours(0,0,0,0);
      start.setDate(start.getDate() - 6);
      relevantDays = dailyRecords.filter(d => new Date(d.date) >= start);
      break;
    }
    case "Monthly": {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      relevantDays = dailyRecords.filter(d => new Date(d.date) >= monthStart);
      break;
    }
    default:
      if (filter?.type === "Custom Date") {
        relevantDays = dailyRecords.filter(
          d => d.date >= filter.from && d.date <= filter.to
        );
      } else {
        relevantDays = dailyRecords;
      }
  }

  // ------- GROUP BY RAW DATE ---------
  const grouped = {};

  relevantDays.forEach(d => {
    if (!grouped[d.date]) grouped[d.date] = 0;
    grouped[d.date] += d.customers;
  });

  // ------- SORT DATES PROPERLY ---------
  const sortedDates = Object.keys(grouped).sort(
    (a, b) => new Date(a) - new Date(b)
  );

  // ------- FORMAT LABELS SAFELY ---------
  const labels = sortedDates.map(dateStr => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  });

  const dataPoints = sortedDates.map(dateStr => grouped[dateStr]);

  return { labels, data: dataPoints };
};


const options = (title) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top", labels: { font: { size: 14, weight: "600" } } },
    title: {
      display: true,
      text: title,
      font: { size: 20, weight: "bold" },
      color: "#222",
      padding: { bottom: 20 },
    },
    tooltip: {
      callbacks: {
        label: ctx => `Customers: ${ctx.raw}`,
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { stepSize: 1, color: "#4b5563" },
      grid: { color: "rgba(0,0,0,0.05)" },
    },
    x: {
      ticks: { color: "#4b5563" },
      grid: { display: false },
    },
  },
});

export default function CustomerDashboard({ filter, dailyRecords }) {
  console.log(dailyRecords)
  const chartData = useMemo(
    () => buildLineData(dailyRecords, filter),
    [dailyRecords, filter]
  );


  const title = (() => {
    if (filter === "Today") return "Today – New Customers";
    if (filter === "Yesterday") return "Yesterday – New Customers";
    if (filter === "Weekly") return "Last 7 Days – New Customers";
    if (filter === "Monthly") return "Current Month – New Customers";
    if (filter?.type === "Custom Date")
      return `${new Date(filter.from).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
      })} – ${new Date(filter.to).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })} – New Customers`;
    return "New Customers Over Time";
  })();

  const data = {
    labels: chartData.labels,
    datasets: [
      {
        fill: true,
        label: "New Customers",
        data: chartData.data,
        borderColor: "rgb(37, 99, 235)",
        backgroundColor: "rgba(37, 99, 235, 0.25)",
        tension: 0.4,
        pointBackgroundColor: "rgb(37, 99, 235)",
        pointRadius: 5,
      },
    ],
  };

  console.log(data)
  return (
    <div className="min-h-[400px] h-full flex flex-col">
      <Line data={data} options={options(title)} />
    </div>
  );
}